/**
 * Add a colleague: a name, an address, a role.
 *
 * NO PASSWORD FIELD, and its absence is the design rather than an omission. An
 * administrator who types a password for somebody else is holding that person's
 * credential and will usually send it over chat. The account is created without
 * one and the person sets their own through a link mailed to the address the
 * account is for, which also proves they control that mailbox.
 *
 * The invitation is a checkbox rather than automatic because there is one real
 * case for skipping it: seating somebody who already has an account and already
 * signs in. Mailing them a "set your password" link would be confusing at best.
 * It defaults to on, because the common case is a new colleague.
 */
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { CrmRoleOption, InviteOutcome } from "../../../../lib/crmPeople";
import { Btn, Modal, SelectInput } from "../../../ui";

interface Props {
  roles: CrmRoleOption[];
  onClose: () => void;
  onDone: (result: { reused: boolean; invited: InviteOutcome }) => void | Promise<void>;
  submit: (body: {
    name: string;
    email: string;
    roleId: string;
    invite: boolean;
  }) => Promise<{ id: string; reused: boolean; invited: InviteOutcome }>;
}

export function AddPersonModal({ roles, onClose, onDone, submit }: Props) {
  const t = useT();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState(roles[0]?.id ?? "");
  const [invite, setInvite] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready = name.trim().length > 0 && email.trim().length > 0 && roleId.length > 0;

  async function save() {
    if (!ready) return;
    setSaving(true);
    setError(null);
    try {
      const result = await submit({ name: name.trim(), email: email.trim(), roleId, invite });
      await onDone(result);
    } catch {
      // The server's own reasons are worth showing separately one day; today
      // the two that can happen are a malformed address and a role that is not
      // this site's, and both read as "check the form".
      setError(t("crmPeople.createError"));
      setSaving(false);
    }
  }

  return (
    <Modal
      title={t("crmPeople.addTitle")}
      label={t("crmPeople.addTitle")}
      onClose={onClose}
      className="max-w-[460px]"
      footer={
        <>
          <Btn tone="plain" onClick={onClose} disabled={saving}>
            {t("common.cancel")}
          </Btn>
          <Btn tone="dark" onClick={() => void save()} disabled={!ready || saving}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : null}
            {t("crmPeople.addSubmit")}
          </Btn>
        </>
      }
    >
      <div className="mt-1 flex flex-col gap-3">
        <div>
          <span className="crm-lab">{t("crmPeople.fieldName")} *</span>
          <label className="crm-inp">
            <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" autoFocus />
          </label>
        </div>

        <div>
          <span className="crm-lab">{t("crmPeople.fieldEmail")} *</span>
          <label className="crm-inp">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          <span className="mt-1 block text-[11.5px] text-muted-foreground">
            {t("crmPeople.fieldEmailHint")}
          </span>
        </div>

        <div>
          <span className="crm-lab">{t("crmPeople.fieldRole")} *</span>
          <SelectInput value={roleId} onChange={setRoleId} label={t("crmPeople.fieldRole")}>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </SelectInput>
        </div>

        {/* The tick box keeps its native look on purpose - see the note beside
            `.crm-inp input[type="checkbox"]` in index.css. */}
        <label className="crm-inp">
          <input type="checkbox" checked={invite} onChange={(e) => setInvite(e.target.checked)} />
          <span className="text-[12.5px] leading-snug">{t("crmPeople.fieldInvite")}</span>
        </label>

        {error ? <span className="block text-[11.5px] text-destructive">{error}</span> : null}
      </div>
    </Modal>
  );
}
